import useUserStore from "@/state/user/useUserStore";
import React, { useEffect } from "react";
import {
  ArrowBigLeft,
  Disc3,
  ExternalLink,
  Eye,
  GraduationCap,
  Shirt,
  Smile,
} from "lucide-react";
import { PostPage } from "@/components/formats/post-page";
import { Asset } from "@/types/types";
import {
  Banner,
  MemeGrid,
  RouteWrapper,
  TitleShadow,
  TitleShadowHenlo2,
  UploadImageContainer,
} from "@/components/styles";
import { ScrollView, Window, WindowContent, WindowHeader } from "react95";
import MemeTemplate from "@/components/ui/meme-template";
import Footer from "@/components/ui/footer";
import { trackEvent } from "@openpanel/nextjs";
import { ValidatorWidget } from "@0xhoneyjar/validator-widget";
import { BannerPage } from "@/components/formats/banner-page";
import { useBackHandler } from "@/lib/hooks/useBackHanlder";
import Image from "@/components/ui/image";
import Link from "next/link";

export const ThjPage = () => {
  const [imageFormat, setRestrictedFonts, assetsCount, setUploadAssets] =
    useUserStore((state) => [
      state.imageFormat,
      state.setRestrictedFonts,
      state.assetsCount,
      state.setUploadAssets,
    ]);

  const TemplateAssets = Array.from(
    { length: assetsCount?.template },
    (_, i) => `/Mememaker/template/template${i + 1}.png`
  ).reverse();

  const HeadAssets = Array.from(
    { length: assetsCount?.base },
    (_, i) => `/Mememaker/base/base${i + 1}.png`
  );

  const ClothingAssets = Array.from(
    { length: assetsCount?.clothing },
    (_, i) => `/Mememaker/clothing/clothing${i + 1}.png`
  );

  const FaceAssets = Array.from(
    { length: assetsCount?.headgear },
    (_, i) => `/Mememaker/headgear/headgear${i + 1}.png`
  );

  const EyeAssets = Array.from(
    { length: assetsCount?.face },
    (_, i) => `/Mememaker/face/face${i + 1}.png`
  );

  const MiscAssets = Array.from(
    { length: assetsCount?.misc },
    (_, i) => `/Mememaker/misc/misc${i + 1}.png`
  ).reverse();

  const allAssets: Asset[] = [
    { assets: HeadAssets, icon: <Smile /> },
    { assets: ClothingAssets, icon: <Shirt /> },
    { assets: FaceAssets, icon: <GraduationCap /> },
    { assets: EyeAssets, icon: <Eye /> },
    { assets: MiscAssets, icon: <Disc3 /> },
  ];

  const backHandler = useBackHandler();

  useEffect(() => {
    backHandler();
    setRestrictedFonts([
      { value: "title1", label: "Honey" },
      { value: "title2", label: "Comic" },
      { value: "title3", label: "Pixel" },
      { value: "title9", label: "Impact" },
      { value: "title10", label: "Lowpoly" },
    ]);
  }, []);

  useEffect(() => {
    setUploadAssets([]);
  }, []);

  return (
    <>
      <RouteWrapper>
        <Window
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            fontFamily: "var(--font-cabin-condensed)",
            overflow: "hidden",
          }}
        >
          <strong>
            <WindowHeader
              className="window-title"
              style={{ background: "#623a00" }}
            >
              <div
                style={{
                  gap: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    position: "relative",
                  }}
                >
                  <Image
                    src={"/Mememaker/beraflip/beraflip.png"}
                    alt={"beraflip"}
                    fill
                    style={{
                      objectFit: "contain",
                    }}
                  />
                </div>
                <span style={{ fontWeight: 500 }}>Beraflip</span>
              </div>
            </WindowHeader>
          </strong>
          <WindowContent
            style={{
              padding: 8,
              boxSizing: "border-box",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ScrollView
              style={{
                width: "100%",
                flexGrow: "1",
                background: "#ddd",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                maxHeight: "calc(100vh - 62px)",
              }}
            >
              {(() => {
                switch (imageFormat) {
                  case "banner":
                    return (
                      <BannerPage
                        color={"#eeba02"}
                        assets={allAssets}
                        websiteLink="https://www.0xhoneyjar.xyz/"
                      />
                    );
                  case "post":
                    return (
                      <PostPage
                        color={"#eeba02"}
                        assets={allAssets}
                        websiteLink="https://www.0xhoneyjar.xyz/"
                      />
                    );
                  default:
                    return (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "space-between",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "auto",
                            display: "flex",
                            flexDirection: "column",
                            boxSizing: "border-box",
                            padding: "36px",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              marginBottom: "16px",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "start",
                                justifyContent: "center",
                                flexDirection: "column",
                              }}
                            >
                              <TitleShadow
                                style={{ margin: 0, marginBottom: "4px" }}
                              >
                                the honey jar
                              </TitleShadow>
                              <p
                                style={{
                                  margin: 0,
                                  fontFamily: "var(--font-cabin-condensed)",
                                }}
                              >
                                Select a preset meme template to start creating
                                your meme.
                              </p>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              <Link
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  fontFamily: "var(--font-cabin-condensed)",
                                  whiteSpace: "nowrap",
                                  textDecoration: "none",
                                  color: "black",
                                }}
                                href="/"
                              >
                                <UploadImageContainer
                                  whileHover={{
                                    scale: 1.02,
                                    transition: {
                                      duration: 0.15,
                                      ease: "linear",
                                    },
                                  }}
                                  whileTap={{ scale: 0.99 }}
                                >
                                  <ArrowBigLeft size={20} />
                                  Back
                                </UploadImageContainer>
                              </Link>
                            </div>
                          </div>
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              flexDirection: "row",
                              gap: "16px",
                            }}
                          >
                            <MemeGrid>
                              {assetsCount?.template > 0 && (
                                <>
                                  {TemplateAssets.map((src) => (
                                    <MemeTemplate key={src} src={src} />
                                  ))}
                                  <MemeTemplate
                                    src={"/Mememaker/background/memeblank1.png"}
                                  />
                                  <MemeTemplate
                                    src={"/Mememaker/background/memeblank2.png"}
                                  />
                                  <MemeTemplate
                                    src={"/Mememaker/background/memeblank3.png"}
                                  />
                                  <MemeTemplate
                                    src={"/Mememaker/background/memeblank4.png"}
                                  />
                                </>
                              )}
                            </MemeGrid>
                          </div>
                        </div>
                        <Footer />
                      </div>
                    );
                }
              })()}
            </ScrollView>
          </WindowContent>
        </Window>
      </RouteWrapper>
    </>
  );
};
